import React from "react";
import { DataTable } from "@/components/ui/datatable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Finding, Audit, CorrectiveAction } from "@/app/pages/auditcentre/index";
import type { ColumnDef } from "@tanstack/react-table";
import { FindingDetailDialog } from "./FindingDetailDialog";
import { FindingCreateDialog } from "./FindingCreateDialog";
import { AuditService } from "@/app/pages/auditcentre/auditService";
import { useTenancy } from "@/contexts/TenancyContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuItem, DropdownMenuSeparator } from "../ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { MoreHorizontal } from "lucide-react";
import { FindingEditDialog } from "./FindingEditDialog";

interface StandaloneNonConformityTableProps {
  findings: (Finding & { auditId?: string })[];
  audits: Audit[];
  onLink: (findingId: string, auditId: string) => void;
  onUnlink: (findingId: string) => void;
  onDelete: (findingId: string) => void;
  onCreate: (finding: { summary: string; owner: string; status: "Open" | "Closed"; type: "Minor NC" | "Major NC" | "OFI"; auditId?: string }) => void;
  onEdit: (findingId: string, updates: Partial<Finding>) => void;
  onActionCreated: (action: CorrectiveAction) => void;
  onActionUpdated: (actionId: string, updates: Partial<CorrectiveAction>) => void;
  onActionDeleted: (actionId: string) => void;
  onSave: (finding: Finding) => Promise<boolean> | boolean | void;
  auditService: AuditService;
  deepLinkedFindingId?: string;
}

export function StandaloneNonConformityTable({
  findings,
  audits,
  onLink,
  onUnlink,
  onDelete,
  onCreate,
  onSave,
  onActionCreated,
  onActionUpdated,
  onActionDeleted,
  auditService,
  deepLinkedFindingId,
}: StandaloneNonConformityTableProps) {
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [findingToDelete, setFindingToDelete] = React.useState<string | null>(null);
  const { userIdToName } = useTenancy();
  const [dialogMode, setDialogMode] = React.useState<null | 'view' | 'edit'>(null);
  const [selectedFinding, setSelectedFinding] = React.useState<Finding & { auditId?: string } | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);

  // Deep-link open: if an id is provided and found in list, open view dialog
  React.useEffect(() => {
    if (!deepLinkedFindingId || !Array.isArray(findings) || findings.length === 0) return;
    const match = findings.find(f => String(f.id) === String(deepLinkedFindingId));
    if (match) {
      setSelectedFinding(match);
      setDialogMode('view');
    }
  }, [deepLinkedFindingId, findings]);

  const auditOptions = audits.map(a => ({ id: a.id, label: `${a.type} — ${a.scope}` }));

  const columns: ColumnDef<Finding & { auditId?: string }>[] = [
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row }) => <Badge variant="medium">{row.original.type}</Badge>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.status === "Open" ? "destructiveSubtle" : "successSubtle"}>{row.original.status}</Badge>
      ),
    },
    {
      accessorKey: "summary",
      header: "Summary",
      cell: ({ row }) => <span>{row.original.summary}</span>,
    },
    {
      accessorKey: "owner",
      header: "Owner",
      cell: ({ row }) => <span>{userIdToName[row.original.owner] === "" || userIdToName[row.original.owner] === undefined ? row.original.owner : userIdToName[row.original.owner]}</span>,
    },
    {
      id: "link",
      header: "Link",
      cell: ({ row }) => (
        row.original.auditId ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Linked to audit: <b>{
                (() => {
                  const audit = audits.find(a => a.id === row.original.auditId);
                  return audit ? `${audit.type} — ${audit.scope}` : row.original.auditId;
                })()
              }</b>
            </span>
            <Button size="xs" variant="outline" onClick={() => onUnlink(row.original.id)}>Unlink</Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Select onValueChange={value => {
              console.log(value)
              onLink(row.original.id, value as unknown as string)
            }}>
              <SelectTrigger className="border rounded px-2 py-1 text-xs">
                <SelectValue placeholder="Select audit" />
              </SelectTrigger>
              <SelectContent>
                {auditOptions.map(opt => (
                  <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )
      ),
    },
    {
      header: "Actions",
      cell: ({ row }) => {
        const incident = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-gray-500">Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={e => {
                e.stopPropagation();
                setSelectedFinding(row.original);
                setDialogMode('view');
              }}>
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={e => {
                e.stopPropagation();
                setSelectedFinding(row.original);
                setDialogMode('edit');
              }}>
                Edit
              </DropdownMenuItem>
              {/* Uncomment and implement onDelete if needed */}
              <DropdownMenuItem onClick={e => {
                e.stopPropagation();
                setFindingToDelete(incident.id);
                setShowDeleteDialog(true);
              }} className="text-red-600">
                Delete
              </DropdownMenuItem>
              {/* DED-1141: Removed audit link from dropdown menu - linking is done via the Link column */}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    }
  ];

  return (
    <>
      <div className="flex justify-end mb-3">
        <Button onClick={() => setCreateDialogOpen(true)} variant="default">+ New Non-Conformity</Button>
      </div>
      {/* Prevent row click from opening View dialog if action dropdown was just used */}
      <DataTable
        columns={columns}
        data={findings}
        pageSize={5}
        onRowClick={(row, event) => {
          // If the event target is inside a dropdown menu, do not open view dialog
          if (event?.target && (event.target as HTMLElement).closest('.dropdown-menu-content')) {
            return;
          }
          setSelectedFinding(row);
          setDialogMode('view');
        }}
      />
      <FindingDetailDialog
        open={dialogMode === 'view'}
        onOpenChange={open => setDialogMode(open ? 'view' : null)}
        finding={selectedFinding}
        onActionCreated={onActionCreated}
        onActionUpdated={onActionUpdated}
        onActionDeleted={onActionDeleted}
        auditService={auditService}
      />
      <FindingCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreate={onCreate}
        auditOptions={auditOptions}
      />
      {/* <FindingEditDialog
        open={dialogMode === 'edit'}
        onOpenChange={open => setDialogMode(open ? 'edit' : null)}
        finding={selectedFinding}
        onSave={onSave}
      /> */}
      <FindingEditDialog
        open={dialogMode === 'edit'}
        onOpenChange={open => setDialogMode(open ? 'edit' : null)}
        finding={selectedFinding}
        onSave={async (updates) => {
          if (!selectedFinding?.id || !onSave) return false;
          const payload = { ...(updates as any), id: selectedFinding.id } as Finding;
          try {
            const result = await (onSave as any)(payload);
            return result === false ? false : true;
          } catch (error) {
            console.error("Failed to update finding:", error);
            return false;
          }
        }}
      />
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Delete Finding</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this finding? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (findingToDelete) {
                  onDelete && onDelete(findingToDelete);
                }
                setShowDeleteDialog(false);
                setFindingToDelete(null);
              }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
